import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { Box } from '@mui/material';
import { useContainerSize } from '../../hooks/useContainerSize';

const ECHARTS_CHART_TYPE = {
  'Line Chart': 'line',
  'Bar Chart': 'bar',
  'Area Chart': 'line',
  'Pie Chart': 'pie',
  'Radar Chart': 'radar',
  'Doughnut Chart': 'pie',
  'Scatter Chart': 'scatter',
};

function buildOption(type, tagprops, liveData, theme) {
  const isDark = theme === 'dark';
  const textColor = isDark ? '#ccc' : '#333';
  const eType = ECHARTS_CHART_TYPE[type] || 'line';

  // Resolve live value per tag
  const resolved = (tagprops || []).map((tp) => {
    const match = liveData.find((d) => String(d.TagId) === String(tp.tagId));
    return {
      name: tp.alias || String(tp.tagId),
      value: parseFloat(match?.value) || 0,
      unit: tp.unit || '',
      min: tp.min != null ? Number(tp.min) : undefined,
      max: tp.max != null ? Number(tp.max) : undefined,
    };
  });

  // ── Pie / Doughnut ────────────────────────────────────────────────────────
  if (type === 'Pie Chart' || type === 'Doughnut Chart') {
    return {
      tooltip: {
        trigger: 'item',
        formatter: ({ name, value, percent }) => `${name}: ${value} (${percent}%)`,
      },
      legend: { textStyle: { color: textColor }, bottom: 0 },
      series: [{
        type: 'pie',
        radius: type === 'Doughnut Chart' ? ['40%', '70%'] : '60%',
        data: resolved.map((r) => ({ name: r.name, value: r.value })),
        label: { color: textColor },
      }],
      backgroundColor: 'transparent',
    };
  }

  // ── Radar ─────────────────────────────────────────────────────────────────
  if (type === 'Radar Chart') {
    return {
      tooltip: { trigger: 'item' },
      legend: { textStyle: { color: textColor }, bottom: 0 },
      radar: {
        indicator: resolved.map((r) => ({
          name: r.name,
          max: r.max ?? Math.max(...resolved.map((x) => x.value), 100),
        })),
        axisName: { color: textColor },
        splitLine: { lineStyle: { color: isDark ? '#333' : '#ddd' } },
      },
      series: [{
        type: 'radar',
        data: [{ value: resolved.map((r) => r.value), name: 'Values' }],
      }],
      backgroundColor: 'transparent',
    };
  }

  // ── Scatter ───────────────────────────────────────────────────────────────
  if (type === 'Scatter Chart') {
    return {
      tooltip: { trigger: 'axis' },
      legend: { textStyle: { color: textColor }, bottom: 0 },
      xAxis: { type: 'category', data: resolved.map((r) => r.name), axisLabel: { color: textColor } },
      yAxis: { type: 'value', axisLabel: { color: textColor } },
      series: resolved.map((r) => ({
        name: r.name,
        type: 'scatter',
        data: [[r.name, r.value]],
      })),
      backgroundColor: 'transparent',
    };
  }

  // ── Line / Bar / Area ─────────────────────────────────────────────────────
  // Derive a shared unit label for Y-axis (use first tag's unit if all same)
  const units = [...new Set(resolved.map((r) => r.unit).filter(Boolean))];
  const yAxisUnit = units.length === 1 ? units[0] : '';

  const series = resolved.map((r) => ({
    name: r.name,
    type: eType,
    data: [r.value],
    areaStyle: type === 'Area Chart' ? { opacity: 0.3 } : undefined,
  }));

  return {
    tooltip: {
      trigger: 'axis',
      formatter: (params) =>
        params.map((p) => {
          const tp = tagprops[p.seriesIndex];
          const unit = tp?.unit || '';
          return `${p.marker}${p.seriesName}: <b>${p.value}${unit ? ' ' + unit : ''}</b>`;
        }).join('<br/>'),
    },
    legend: { textStyle: { color: textColor }, bottom: 0 },
    xAxis: { type: 'category', data: ['Now'], axisLabel: { color: textColor } },
    yAxis: {
      type: 'value',
      axisLabel: {
        color: textColor,
        formatter: yAxisUnit ? `{value} ${yAxisUnit}` : '{value}',
      },
      min: resolved[0]?.min,
      max: resolved[0]?.max,
    },
    series,
    backgroundColor: 'transparent',
  };
}

function GeneralChartWidget({ widget, data, theme }) {
  const liveData = Array.isArray(data) ? data : [];
  const tagprops = widget?.props?.extended?.tagprops || [];
  const option = useMemo(
    () => buildOption(widget.type, tagprops, liveData, theme),
    [widget.type, tagprops, liveData, theme]
  );
  const { ref, width: cw, height: ch } = useContainerSize(64, 64);
  const h = Math.max(48, ch);
  const w = Math.max(48, cw);

  return (
    <Box ref={ref} sx={{ flex: 1, minHeight: 0, width: '100%', overflow: 'hidden' }}>
      {cw > 0 && ch > 0 ? (
        <ReactECharts option={option} style={{ width: w, height: h }} opts={{ renderer: 'canvas' }} />
      ) : null}
    </Box>
  );
}

export default React.memo(GeneralChartWidget);
